import React, { useState, useEffect } from 'react';
import notificationService from '../services/notificationService';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery,
  Avatar,
  Badge,
  Divider,
  Menu,
  MenuItem,
  Tooltip,
  ListItemButton,
  TextField,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Receipt as ReceiptIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  CreditCard as CreditCardIcon,
  Assessment as AssessmentIcon,
  ExitToApp as LogoutIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Home as HomeIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ShoppingCartIcon,
  Assessment as ReportsIcon,
  Folder as DocumentsIcon,
  KeyboardArrowRight as ArrowRightIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Shortcut as ShortcutIcon,
  Search as SearchIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Person as PersonIcon,
  NoteAdd as CreditNoteIcon,
  NoteAlt as DebitNoteIcon,
  Group as TeamIcon,
  Category as CategoryIcon,
  VerifiedUser as VerifiedUserIcon,
  Add as AddIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
  Circle as CircleIcon,
} from '@mui/icons-material';
import KeyboardShortcutHandler from './KeyboardShortcutHandler';
import BusinessProfileForm from './BusinessProfileForm';
import PersonalProfileForm from './PersonalProfileForm';
import { useAuth } from '../context/AuthContext';
import { APP_VERSION } from '../version';

// Default drawer width when expanded
const drawerWidth = 240;
// Collapsed drawer width
const collapsedDrawerWidth = 65;

const menuItems = [
  { 
    text: 'Dashboard', 
    icon: <DashboardIcon />, 
    path: '/dashboard' 
  },
  { 
    type: 'divider',
    text: 'Masters'
  },
  { 
    text: 'Customers', 
    icon: <PeopleIcon />, 
    path: '/customers',
    section: 'masters',
    addPath: '/customers/new'
  },
  { 
    text: 'Suppliers', 
    icon: <BusinessIcon />, 
    path: '/suppliers',
    section: 'masters',
    addPath: '/suppliers/new'
  },
  { 
    text: 'Items', 
    icon: <InventoryIcon />, 
    path: '/items',
    section: 'masters',
    addPath: '/items/new'
  },
  { 
    type: 'divider',
    text: 'Transactions'
  },
  { 
    text: 'Sales', 
    icon: <ShoppingCartIcon />, 
    path: '/sales',
    section: 'transactions',
    addPath: 'sales/new-invoice'
  },
  { 
    text: 'Quotation', 
    icon: <DescriptionIcon />, 
    path: '/quotation',
    section: 'transactions',
    addPath: '/quotation/new'
  },
  { 
    text: 'Purchases', 
    icon: <ReceiptIcon />, 
    path: '/purchases',
    section: 'transactions',
    addPath: '/purchase-invoice'
  },
  { 
    text: 'Credit Notes', 
    icon: <CreditNoteIcon />, 
    path: '/credit-notes',
  },
  { 
    text: 'Debit Notes', 
    icon: <DebitNoteIcon />, 
    path: '/debit-notes',
  },
  { 
    type: 'divider',
    text: 'Others'
  },
  { 
    text: 'ZATCA Details', 
    icon: <VerifiedUserIcon />, 
    path: '/zatca-details' 
  },
  { 
    text: 'Reports', 
    icon: <ReportsIcon />, 
    path: '/reports' 
  },
  { 
    text: 'Team Members', 
    icon: <TeamIcon />, 
    path: '/team-members' 
  },
  { 
    text: 'Other Masters', 
    icon: <CategoryIcon />, 
    path: '/other-masters' 
  },
];

const Layout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchAnchorEl, setSearchAnchorEl] = useState(null);
  const [businessProfileOpen, setBusinessProfileOpen] = useState(false);
  const [personalProfileOpen, setPersonalProfileOpen] = useState(false);
  const [formsAnchorEl, setFormsAnchorEl] = useState(null);
  const [reportsAnchorEl, setReportsAnchorEl] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, currentTenant, logout } = useAuth();

  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      setNotificationsLoading(true);
      console.log('Fetching notifications...');
      const data = await notificationService.getNotifications({ limit: 10 });
      console.log('Notifications data received:', data);
      console.log('Unread count:', data.unreadCount);
      setNotifications(data.notifications || []);
      setUnreadCount(data.unreadCount || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      console.error('Error details:', error.response?.data || error.message);
    } finally {
      setNotificationsLoading(false);
    }
  };

  // Fetch notifications on mount
  useEffect(() => {
    fetchNotifications();
    
    // Poll for new notifications every 5 minutes (300000ms) instead of 30 seconds
    // This reduces server load significantly
    const interval = setInterval(fetchNotifications, 300000);
    
    return () => clearInterval(interval);
  }, []);

  // Get icon component by icon name
  const getIconComponent = (iconName) => {
    const iconMap = {
      CheckCircle: <CheckCircleIcon />,
      Warning: <WarningIcon />,
      Info: <InfoIcon />,
      Error: <ErrorIcon />
    };
    return iconMap[iconName] || <InfoIcon />;
  };

  // Format time ago
  const formatTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    try {
      if (!notification.read) {
        await notificationService.markAsRead(notification._id);
        fetchNotifications(); // Refresh notifications
      }
      if (notification.link) {
        navigate(notification.link);
      }
      handleNotificationsClose();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Sample forms data - replace with your actual forms data
  const forms = [
    { id: 1, title: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
    { id: 2, title: 'Customers', path: '/customers', icon: <PeopleIcon /> },
    { id: 3, title: 'Suppliers', path: '/suppliers', icon: <BusinessIcon /> },
    { id: 4, title: 'Items', path: '/items', icon: <InventoryIcon /> },
    { id: 5, title: 'Sales', path: '/sales', icon: <ShoppingCartIcon /> },
    { id: 6, title: 'Purchases', path: '/purchases', icon: <ReceiptIcon /> },
    { id: 7, title: 'Reports', path: '/reports', icon: <ReportsIcon /> },
    { id: 8, title: 'Other Masters', path: '/other-masters', icon: <CategoryIcon /> },
  ];

  const handleSearch = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    setSearchAnchorEl(event.currentTarget);

    if (query.trim() === '') {
      setSearchResults([]);
      return;
    }

    const results = forms.filter(form => 
      form.title.toLowerCase().includes(query.toLowerCase())
    );
    setSearchResults(results);
  };

  const handleSearchClose = () => {
    setSearchQuery('');
    setSearchResults([]);
    setSearchAnchorEl(null);
  };

  const handleFormClick = (path) => {
    navigate(path);
  };

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    handleProfileMenuClose();
    setPersonalProfileOpen(true);
  };

  const handleNotificationsOpen = async (event) => {
    setNotificationsAnchorEl(event.currentTarget);
    
    // Fetch fresh notifications when user opens the panel
    await fetchNotifications();
    
    // Mark all notifications as read when opening the panel
    if (unreadCount > 0) {
      try {
        console.log('Marking all notifications as read on panel open...');
        await notificationService.markAllAsRead();
        // Refresh notifications to update the count
        await fetchNotifications();
      } catch (error) {
        console.error('Error marking notifications as read:', error);
      }
    }
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
  };

  const handleDrawerHover = (event) => {
    if (!isMobile) {
      setIsHovered(true);
    }
  };

  const handleDrawerLeave = (event) => {
    if (!isMobile) {
      setIsHovered(false);
    }
  };

  const drawer = (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100vh',
      minHeight: '100vh',
      backgroundColor: '#ffffff',
      pt: 0.5,
      overflow: 'hidden',
      borderRight: '1px solid rgba(0, 0, 0, 0.08)',
    }}>
      {/* Logo and Toggle Section */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: (!isCollapsed || isHovered) ? 'space-between' : 'center',
        alignItems: 'center',
        p: 1,
        px: (!isCollapsed || isHovered) ? 1.5 : 1,
        transition: 'all 0.3s ease'
      }}>
        {(!isCollapsed || isHovered) ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              component="img"
              src="/logo/A4 - 17.png"
              alt="Eazy Fatoora"
              sx={{
                height: 35,
                width: 'auto',
                objectFit: 'contain',
                transition: 'all 0.3s ease'
              }}
            />
            <Typography
              sx={{
                fontSize: '1.1rem',
                fontWeight: 600,
                color: 'primary.main',
                letterSpacing: '0.5px'
              }}
            >
              Eazy Fatoora
            </Typography>
          </Box>
        ) : (
          <Box
            component="img"
            src="/logo/A4 - 17.png"
            alt="Eazy Fatoora"
            sx={{
              width: 40,
              height: 40,
              objectFit: 'contain',
              transition: 'all 0.3s ease'
            }}
          />
        )}
        
        {/* Toggle Button - Only show on desktop */}
        {!isMobile && (!isCollapsed || isHovered) && (
          <Tooltip title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"} placement="right">
            <IconButton
              onClick={() => setIsCollapsed(!isCollapsed)}
              size="small"
              sx={{
                bgcolor: 'rgba(67, 97, 238, 0.08)',
                color: 'primary.main',
                width: 28,
                height: 28,
                '&:hover': {
                  bgcolor: 'rgba(67, 97, 238, 0.15)',
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              {isCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Divider sx={{ mb: 0.5, opacity: 0.6 }} />
      
      <List sx={{ 
        flexGrow: 1, 
        px: 0,
        maxHeight: 'calc(100vh - 80px)',
        overflowY: (!isCollapsed || isHovered) ? 'auto' : 'hidden',
        '&::-webkit-scrollbar': {
          width: (!isCollapsed || isHovered) ? '6px' : '0px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'rgba(0, 0, 0, 0.02)',
          borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(67, 97, 238, 0.3)',
          borderRadius: '10px',
          '&:hover': {
            background: 'rgba(67, 97, 238, 0.5)',
          },
        },
        scrollbarWidth: (!isCollapsed || isHovered) ? 'thin' : 'none',
        scrollbarColor: (!isCollapsed || isHovered) ? 'rgba(67, 97, 238, 0.3) transparent' : 'transparent transparent',
        transition: 'all 0.3s ease'
      }}>
        {menuItems.map((item, index) => (
          <React.Fragment key={index}>
            {item.type === 'divider' ? (
              (!isCollapsed || isHovered) && (
                <Box sx={{ 
                  px: 2, 
                  py: 1.5,
                  mt: 1,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: '0.65rem',
                      letterSpacing: '1px',
                      opacity: (!isCollapsed || isHovered) ? 1 : 0,
                      transition: 'opacity 0.3s ease'
                    }}
                  >
                    {item.text}
                  </Typography>
                </Box>
              )
            ) : (
            <Tooltip 
              title={isCollapsed && !isHovered ? item.text : ''} 
              placement="right"
              arrow
            >
              <ListItem 
                disablePadding 
                sx={{ 
                  mb: 0.5,
                  px: (isCollapsed && !isHovered) ? 1 : 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative'
                }}
                onMouseEnter={() => setHoveredItem(index)}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <ListItemButton
                    component={Link}
                    to={item.path}
                    selected={location.pathname === item.path}
                  sx={{
                    px: (isCollapsed && !isHovered) ? 0.75 : 1.5,
                    py: (isCollapsed && !isHovered) ? 0.75 : 1,
                    borderRadius: '6px',
                    mx: (isCollapsed && !isHovered) ? 0 : 1,
                    minHeight: (isCollapsed && !isHovered) ? '40px' : 'auto',
                    width: (isCollapsed && !isHovered) ? '40px' : '100%',
                    maxWidth: (isCollapsed && !isHovered) ? '40px' : '100%',
                    backgroundColor: location.pathname === item.path ? 'primary.main' : 'transparent',
                    color: location.pathname === item.path ? 'white' : 'text.secondary',
                    position: 'relative',
                    backgroundImage: location.pathname === item.path ? 'linear-gradient(135deg, #4361ee 0%, #4895ef 100%)' : 'none',
                    boxShadow: location.pathname === item.path ? '0 2px 6px rgba(67, 97, 238, 0.2)' : 'none',
                    '&:hover': {
                      backgroundColor: location.pathname === item.path ? 'primary.main' : 'rgba(0, 0, 0, 0.04)',
                      boxShadow: location.pathname === item.path ? '0 4px 12px rgba(67, 97, 238, 0.3)' : 'none',
                      '& .MuiListItemIcon-root': {
                        color: location.pathname === item.path ? 'white' : 'primary.main',
                      },
                      '& .MuiListItemText-primary': {
                        color: location.pathname === item.path ? 'white' : 'text.primary',
                      }
                    },
                    justifyContent: (isCollapsed && !isHovered) ? 'center' : 'flex-start',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    pr: (!isCollapsed || isHovered) && item.addPath && hoveredItem === index ? 5 : ((isCollapsed && !isHovered) ? 1 : 1.5),
                    overflow: 'hidden',
                  }}
                >
                  <ListItemIcon sx={{ 
                    minWidth: (isCollapsed && !isHovered) ? 'auto' : 40,
                    width: (isCollapsed && !isHovered) ? '100%' : 'auto',
                    color: location.pathname === item.path ? 'white' : 'rgba(0, 0, 0, 0.54)',
                    '& .MuiSvgIcon-root': {
                      fontSize: (isCollapsed && !isHovered) ? '1.25rem' : '1.2rem',
                      fontWeight: 400,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'color 0.3s ease',
                  }}>
                    {item.icon}
                  </ListItemIcon>
                    <ListItemText 
                      primary={item.text} 
                    sx={{ 
                      opacity: (!isCollapsed || isHovered) ? 1 : 0,
                      display: (!isCollapsed || isHovered) ? 'block' : 'none',
                      transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '& .MuiTypography-root': {
                        fontSize: '0.875rem',
                        fontWeight: location.pathname === item.path ? 600 : 400,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        letterSpacing: '0.2px',
                        color: location.pathname === item.path ? 'white' : 'text.secondary',
                        transition: 'color 0.3s ease',
                      }
                      }} 
                    />
                </ListItemButton>
                {(!isCollapsed || isHovered) && item.addPath && hoveredItem === index && (
                  <Tooltip title={`Add new ${item.text.toLowerCase()}`} placement="right">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigate(item.addPath);
                      }}
                      sx={{
                        position: 'absolute',
                        right: 12,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        bgcolor: 'primary.main',
                        color: 'white',
                        width: 24,
                        height: 24,
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover': {
                          bgcolor: 'primary.dark',
                          transform: 'translateY(-50%) scale(1.15) rotate(90deg)',
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: '0 2px 8px rgba(67, 97, 238, 0.4)',
                      }}
                    >
                      <AddIcon sx={{ fontSize: '0.9rem' }} />
                    </IconButton>
                  </Tooltip>
                )}
              </ListItem>
            </Tooltip>
            )}
          </React.Fragment>
        ))}
      </List>
      
      {/* Version Number at Bottom */}
      <Box sx={{ 
        mt: 'auto',
        p: (!isCollapsed || isHovered) ? 2 : 1,
        borderTop: '1px solid rgba(0, 0, 0, 0.08)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.3s ease'
      }}>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontSize: '0.7rem',
            opacity: (!isCollapsed || isHovered) ? 0.6 : 0,
            display: (!isCollapsed || isHovered) ? 'block' : 'none',
            transition: 'opacity 0.3s ease',
            textAlign: 'center'
          }}
        >
          v{APP_VERSION}
        </Typography>
      </Box>
    </Box>
  );

  // Calculate the current drawer width based on collapsed and hover state
  const currentDrawerWidth = (!isCollapsed || isHovered) ? drawerWidth : collapsedDrawerWidth;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      <KeyboardShortcutHandler />
      
      <Box
        component="nav"
        sx={{ 
          width: { sm: currentDrawerWidth }, 
          flexShrink: { sm: 0 },
          position: 'fixed',
          height: '100vh',
          overflowY: 'auto',
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&::after': {
            content: '""',
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: '1px',
            background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.08), rgba(0, 0, 0, 0.12))',
          }
        }}
      >
        {isMobile ? (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                borderRight: '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: 'none',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(250, 252, 255, 0.95) 100%)',
              },
            }}
          >
            {drawer}
          </Drawer>
        ) : (
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', sm: 'block' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: currentDrawerWidth,
                height: '100vh',
                minHeight: '100vh',
                borderRight: 'none',
                boxShadow: '2px 0 8px rgba(0, 0, 0, 0.04)',
                background: '#ffffff',
                backdropFilter: 'blur(10px)',
                transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                overflowX: 'hidden',
                '&::-webkit-scrollbar': {
                  width: '0px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'transparent',
                },
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  bottom: 0,
                  width: '1px',
                  background: 'rgba(0, 0, 0, 0.08)',
                }
              },
            }}
            open
          >
            <Box
              onMouseEnter={handleDrawerHover}
              onMouseLeave={handleDrawerLeave}
              sx={{ 
                height: '100%',
                position: 'relative',
              }}
            >
              {drawer}
            </Box>
          </Drawer>
        )}
      </Box>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { sm: `${currentDrawerWidth}px` },
          backgroundColor: '#ffffff',
          minHeight: '100vh',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          boxShadow: 'none',
          overflow: 'auto',
          maxWidth: '100%',
        }}
      >
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { sm: `${currentDrawerWidth}px` },
            bgcolor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          color: 'text.primary',
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
            height: 60,
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(250, 252, 255, 0.95) 100%)',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
            borderRadius: 0,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          }}
        >
          <Toolbar 
            sx={{ 
              px: { xs: 1.5, sm: 3 }, 
              height: '100%', 
              minHeight: '60px !important',
              borderRadius: 0,
            }}
          >
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
                mr: 1.5, 
              display: { sm: 'none' },
                borderRadius: 0,
              bgcolor: 'rgba(67, 97, 238, 0.05)',
              '&:hover': {
                bgcolor: 'rgba(67, 97, 238, 0.1)',
                },
            }}
          >
            <MenuIcon />
          </IconButton>

            {/* Navbar Links */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button
                component={Link}
                to="/"
            sx={{ 
                  color: 'text.primary',
                  textTransform: 'none',
                  fontWeight: 400,
                  fontSize: '0.875rem',
                  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  '&:hover': {
                    color: 'primary.main',
              bgcolor: 'rgba(67, 97, 238, 0.05)',
                  },
                }}
              >
                Home
              </Button>
              <Button
                component={Link}
                to="/dashboard"
                sx={{
                  color: 'text.primary',
                  textTransform: 'none',
                  fontWeight: 400,
                  fontSize: '0.875rem',
                  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  '&:hover': {
                    color: 'primary.main',
                    bgcolor: 'rgba(67, 97, 238, 0.05)',
                  },
                }}
              >
                Dashboard
              </Button>
              <Button
                component={Link}
                to="/sales"
                sx={{
                  color: 'text.primary',
                  textTransform: 'none',
                  fontWeight: 400,
                  fontSize: '0.875rem',
                  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  '&:hover': {
                    color: 'primary.main',
                    bgcolor: 'rgba(67, 97, 238, 0.05)',
                  },
                }}
              >
                Sales
              </Button>
              
              {/* Forms Dropdown */}
              <Button
                onClick={(e) => setFormsAnchorEl(e.currentTarget)}
                sx={{
                  color: 'text.primary',
                  textTransform: 'none',
                  fontWeight: 400,
                  fontSize: '0.875rem',
                  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  '&:hover': {
                    color: 'primary.main',
                    bgcolor: 'rgba(67, 97, 238, 0.05)',
                  },
                }}
              >
                Forms
                <KeyboardArrowDownIcon sx={{ ml: 0.5 }} />
              </Button>

              {/* Forms Dropdown Menu */}
      <Menu
                anchorEl={formsAnchorEl}
                open={Boolean(formsAnchorEl)}
                onClose={() => setFormsAnchorEl(null)}
                PaperProps={{
                  elevation: 2,
                  sx: { 
                    mt: 1,
                    minWidth: 200,
                    borderRadius: '4px',
                    overflow: 'hidden',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  }
                }}
                transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
              >
                <MenuItem 
                  component={Link}
                  to="/sales-invoice"
                  onClick={() => setFormsAnchorEl(null)}
                  sx={{
                    py: 1.2,
              px: 2,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'rgba(67, 97, 238, 0.05)',
                    }
                  }}
                >
                  <ListItemIcon>
                    <ReceiptIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
                  </ListItemIcon>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                      fontWeight: 500
                    }}
                  >
                    Sales Invoice
                  </Typography>
                </MenuItem>
                <MenuItem 
                  component={Link}
                  to="/purchase-invoice"
                  onClick={() => setFormsAnchorEl(null)}
                  sx={{
                    py: 1.2,
                    px: 2,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'rgba(67, 97, 238, 0.05)',
                    }
                  }}
                >
                  <ListItemIcon>
                    <ShoppingCartIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
                  </ListItemIcon>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                      fontWeight: 500
                    }}
                  >
                    Purchase Invoice
                  </Typography>
                </MenuItem>
                <MenuItem 
                  component={Link}
                  to="/credit-note"
                  onClick={() => setFormsAnchorEl(null)}
                  sx={{
                    py: 1.2,
                    px: 2,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'rgba(67, 97, 238, 0.05)',
                    }
                  }}
                >
                  <ListItemIcon>
                    <CreditNoteIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
                  </ListItemIcon>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                      fontWeight: 500
                    }}
                  >
                    Credit Note
                  </Typography>
                </MenuItem>
                <MenuItem 
                  component={Link}
                  to="/debit-note"
                  onClick={() => setFormsAnchorEl(null)}
                  sx={{
                    py: 1.2,
                    px: 2,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'rgba(67, 97, 238, 0.05)',
                    }
                  }}
                >
                  <ListItemIcon>
                    <DebitNoteIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
                  </ListItemIcon>
                  <Typography variant="body2">Debit Note</Typography>
                </MenuItem>
              </Menu>

              {/* Reports Dropdown */}
              <Button
                onClick={(e) => setReportsAnchorEl(e.currentTarget)}
                sx={{
                  color: 'text.primary',
                  textTransform: 'none',
                  fontWeight: 400,
                  borderRadius: 0,
                  '&:hover': {
                    color: 'primary.main',
                    bgcolor: 'transparent',
                  },
                }}
              >
                Reports
                <KeyboardArrowDownIcon sx={{ ml: 0.5 }} />
              </Button>

              {/* Reports Dropdown Menu */}
              <Menu
                anchorEl={reportsAnchorEl}
                open={Boolean(reportsAnchorEl)}
                onClose={() => setReportsAnchorEl(null)}
                PaperProps={{
                  elevation: 2,
                  sx: {
                    mt: 1,
                    minWidth: 200,
                    borderRadius: 0,
                    overflow: 'hidden',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  }
                }}
                transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
              >
                  <MenuItem
                  component={Link}
                  to="/sales-report"
                  onClick={() => setReportsAnchorEl(null)}
                    sx={{
                      py: 1.2,
            px: 2,
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: 'rgba(67, 97, 238, 0.05)',
                      }
                    }}
                  >
                    <ListItemIcon>
                    <AssessmentIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
                    </ListItemIcon>
                  <Typography variant="body2">Sales Report</Typography>
                  </MenuItem>
                <MenuItem 
                  component={Link}
                  to="/purchase-report"
                  onClick={() => setReportsAnchorEl(null)}
                  sx={{
                    py: 1.2,
                    px: 2,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'rgba(67, 97, 238, 0.05)',
                    }
                  }}
                >
                  <ListItemIcon>
                    <AssessmentIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
                  </ListItemIcon>
                  <Typography variant="body2">Purchase Report</Typography>
                </MenuItem>
                <MenuItem 
                  component={Link}
                  to="/vat-report"
                  onClick={() => setReportsAnchorEl(null)}
                  sx={{
                    py: 1.2,
                    px: 2,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'rgba(67, 97, 238, 0.05)',
                    }
                  }}
                >
                  <ListItemIcon>
                    <AssessmentIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
                  </ListItemIcon>
                  <Typography variant="body2">VAT Report</Typography>
                </MenuItem>
            </Menu>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* Theme Toggle */}
            <IconButton
              onClick={toggleTheme}
              sx={{ 
                  borderRadius: 0,
                  p: 0.75,
                '&:hover': {
                  bgcolor: 'rgba(67, 97, 238, 0.08)',
                  },
              }}
            >
              {isDarkMode ? 
                  <LightModeIcon sx={{ color: '#4361ee', fontSize: '1.1rem' }} /> : 
                  <DarkModeIcon sx={{ color: '#4361ee', fontSize: '1.1rem' }} />
              }
            </IconButton>

            {/* Notifications */}
            <IconButton
              onClick={handleNotificationsOpen}
              sx={{ 
                  borderRadius: 0,
                  p: 0.75,
                '&:hover': {
                  bgcolor: 'rgba(67, 97, 238, 0.08)',
                  },
                  animation: unreadCount > 0 ? 'bellSwing 2s ease-in-out infinite' : 'none',
                  '@keyframes bellSwing': {
                    '0%': {
                      transform: 'rotate(0deg)',
                    },
                    '25%': {
                      transform: 'rotate(15deg)',
                    },
                    '50%': {
                      transform: 'rotate(0deg)',
                    },
                    '75%': {
                      transform: 'rotate(-15deg)',
                    },
                    '100%': {
                      transform: 'rotate(0deg)',
                    },
                  },
              }}
            >
              <Badge 
                badgeContent={unreadCount} 
                color="error"
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '10px',
                    height: '18px',
                    minWidth: '18px',
                    padding: '0 4px',
                      border: '2px solid white',
                  }
                }}
              >
                  <NotificationsIcon sx={{ color: '#4361ee', fontSize: '1.1rem' }} />
              </Badge>
            </IconButton>

            {/* Settings */}
            <IconButton
              onClick={() => navigate('/settings')}
              sx={{ 
                borderRadius: 0,
                p: 0.75,
                '&:hover': {
                  bgcolor: 'rgba(67, 97, 238, 0.08)',
                },
              }}
            >
              <SettingsIcon sx={{ color: '#4361ee', fontSize: '1.1rem' }} />
            </IconButton>

              {/* Profile */}
            <Box 
              onClick={handleProfileMenuOpen}
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                  ml: 2,
                '&:hover': {
                    '& .MuiTypography-root': {
                      color: 'primary.main',
                    },
                    '& .MuiSvgIcon-root': {
                      color: 'primary.main',
                    }
                }
              }}
            >
              <Avatar 
                sx={{ 
                    width: 36, 
                    height: 36,
                    bgcolor: 'primary.main',
                    color: 'white',
                    fontSize: '1rem',
                    fontWeight: 'bold'
                  }}
                >
                  {currentUser?.username ? currentUser.username.charAt(0).toUpperCase() : 'U'}
              </Avatar>
                <Box sx={{ ml: 1, display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography 
                    variant="body2" 
        sx={{ 
                      fontWeight: 500,
                      color: 'text.primary',
                      transition: 'color 0.2s',
                    }}
                  >
                    {currentUser?.username || 'User'}
                  </Typography>
                  <Typography 
                    variant="caption" 
            sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                    }}
                  >
                    {currentTenant?.name || 'Company 1'}
                  </Typography>
                </Box>
                <KeyboardArrowDownIcon 
            sx={{
                    color: 'text.secondary',
                    fontSize: '1.1rem',
              display: { xs: 'none', sm: 'block' },
                    ml: 0.5
                  }} 
                />
            </Box>
      </Box>
          </Toolbar>
        </AppBar>

        <Box sx={{ p: 3, mt: 6, boxShadow: 'none' }}>
        <Outlet />
      </Box>
      </Box>
      
      {/* Business Profile Form */}
      <BusinessProfileForm
        open={businessProfileOpen}
        onClose={() => setBusinessProfileOpen(false)}
      />

      {/* Personal Profile Form */}
      <PersonalProfileForm
        open={personalProfileOpen}
        onClose={() => setPersonalProfileOpen(false)}
      />

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          elevation: 2,
          sx: { 
            mt: 1,
            minWidth: 200,
            borderRadius: 0,
            overflow: 'hidden',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem 
          sx={{
            py: 1.5,
            px: 2,
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          }}
          onClick={handleProfileMenuClose}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              {currentUser?.username || 'User'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              {currentUser?.email || 'user@example.com'}
            </Typography>
            {currentTenant && (
              <Typography variant="body2" color="primary" sx={{ fontSize: '0.8rem', mt: 0.5 }}>
                {currentTenant.name || 'Organization'}
              </Typography>
            )}
          </Box>
        </MenuItem>
        <MenuItem 
          onClick={handleProfileClick}
          sx={{
            py: 1.2,
            px: 2,
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: 'rgba(67, 97, 238, 0.05)',
            }
          }}
        >
          <ListItemIcon>
            <PersonIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
          </ListItemIcon>
          <Typography variant="body2">Personal Profile</Typography>
        </MenuItem>
        <MenuItem 
          onClick={() => {
            handleProfileMenuClose();
            setBusinessProfileOpen(true);
          }}
          sx={{
            py: 1.2,
            px: 2,
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: 'rgba(67, 97, 238, 0.05)',
            }
          }}
        >
          <ListItemIcon>
            <BusinessIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
          </ListItemIcon>
          <Typography variant="body2">Business Profile</Typography>
        </MenuItem>
        <MenuItem 
          onClick={() => {
            handleProfileMenuClose();
            navigate('/settings');
          }}
          sx={{
            py: 1.2,
            px: 2,
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: 'rgba(67, 97, 238, 0.05)',
            }
          }}
        >
          <ListItemIcon>
            <SettingsIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
          </ListItemIcon>
          <Typography variant="body2">Settings</Typography>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem 
          onClick={handleLogout}
          sx={{
            py: 1.2,
            px: 2,
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: 'rgba(255, 0, 0, 0.05)',
              '& .MuiListItemIcon-root': {
                color: 'error.main',
              },
              '& .MuiTypography-root': {
                color: 'error.main',
              },
            }
          }}
        >
          <ListItemIcon>
            <LogoutIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
          </ListItemIcon>
          <Typography variant="body2">Logout</Typography>
        </MenuItem>
      </Menu>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationsAnchorEl}
        open={Boolean(notificationsAnchorEl)}
        onClose={handleNotificationsClose}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1.5,
            width: 'auto',
            minWidth: 320,
            maxWidth: 380,
            maxHeight: 400,
            borderRadius: '12px',
            overflow: 'hidden',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Notifications Header */}
        <Box
          sx={{
            px: 2,
            py: 1,
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
            background: 'linear-gradient(135deg, #4361ee 0%, #4895ef 100%)',
            color: 'white',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                Notifications
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.65rem' }}>
                {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
            <NotificationsIcon sx={{ fontSize: '1.1rem' }} />
          </Box>
        </Box>

        {/* Notifications List */}
        <Box sx={{ 
          maxHeight: 240, 
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '0px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'transparent',
          },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}>
          {notificationsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">Loading...</Typography>
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">No notifications</Typography>
            </Box>
          ) : (
            notifications.map((notification, index) => (
            <MenuItem
              key={notification._id}
              onClick={() => handleNotificationClick(notification)}
              sx={{
                px: 1.5,
                py: 1,
                borderBottom: index < notifications.length - 1 ? '1px solid rgba(0, 0, 0, 0.06)' : 'none',
                backgroundColor: notification.read ? 'transparent' : 'rgba(67, 97, 238, 0.03)',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: 'rgba(67, 97, 238, 0.08)',
                  transform: 'translateX(4px)',
                },
                display: 'flex',
                alignItems: 'center',
                gap: 1.2,
                minHeight: 'auto',
              }}
            >
              {/* Notification Icon */}
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: `${notification.color}15`,
                  color: notification.color,
                  flexShrink: 0,
                }}
              >
                {React.cloneElement(getIconComponent(notification.icon), { sx: { fontSize: '1rem' } })}
              </Box>

              {/* Notification Content */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      color: 'text.primary',
                      flex: 1,
                      lineHeight: 1.2,
                    }}
                  >
                    {notification.title}
                  </Typography>
                  {!notification.read && (
                    <CircleIcon
                      sx={{
                        fontSize: '6px',
                        color: '#4361ee',
                      }}
                    />
                  )}
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    mb: 0.2,
                    lineHeight: 1.25,
                  }}
                >
                  {notification.message}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.disabled',
                    fontSize: '0.6rem',
                    fontStyle: 'italic',
                    lineHeight: 1,
                  }}
                >
                  {formatTimeAgo(notification.createdAt)}
                </Typography>
              </Box>
            </MenuItem>
          ))
          )}
        </Box>
      </Menu>

    </Box>
  );
}

export default Layout;